import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import Seo from "@/lib/Seo";
import { useFetch } from "@/lib/api";
import { FadeUp } from "@/lib/motion";
import { useQuote } from "@/lib/quote";
import Breadcrumbs from "@/components/Breadcrumbs";
import ProductCard from "@/components/ProductCard";

export default function ShopItem() {
  const { slug } = useParams();
  const { data: item, loading, error } = useFetch(`/catalog/${slug}`);
  const { data: catalog } = useFetch("/catalog");
  const { addItem, setDrawerOpen } = useQuote();
  const [options, setOptions] = useState("");
  const [quantity, setQuantity] = useState(1);

  if (loading) {
    return (
      <div className="flex min-h-[70vh] items-center justify-center pt-[72px]">
        <p className="text-smoke" data-testid="shop-item-loading">Opening the detail…</p>
      </div>
    );
  }
  if (error || !item) {
    return (
      <div className="flex min-h-[70vh] flex-col items-center justify-center gap-6 px-6 pt-[72px] text-center">
        <h1 className="font-display text-4xl text-ink">We could not find that piece.</h1>
        <Link to="/shop" data-testid="shop-item-back-to-shop" className="rounded-none bg-olive px-8 py-3 text-[12px] uppercase tracking-[0.18em] text-ivory">
          Back to the Collection
        </Link>
      </div>
    );
  }

  const related = (catalog || []).filter((c) => (item.related || []).includes(c.slug)).slice(0, 3);

  const add = () => {
    for (let k = 0; k < quantity; k++) addItem(item, options.trim());
    setDrawerOpen(true);
  };

  return (
    <div className="pt-[72px]">
      <Seo title={`${item.title} — Personalized Wedding Detail | Marble & Veil`} description={item.description.slice(0, 155)} path={`/shop/${item.slug}`} />
      <section className="mx-auto max-w-7xl px-5 py-12 sm:px-8 lg:py-20">
        <Breadcrumbs trail={[["Home", "/"], ["Shop", "/shop"], [item.category, "/shop"], [item.title]]} />
        <div className="mt-10 grid gap-12 lg:grid-cols-2 lg:gap-16">
          <FadeUp>
            <div className="overflow-hidden bg-parch">
              <img src={item.image} alt={`${item.title} — design inspiration`} className="aspect-[4/5] w-full object-cover" width="900" height="1125" data-testid="shop-item-image" />
            </div>
          </FadeUp>
          <FadeUp delay={0.1}>
            <div className="lg:sticky lg:top-28">
              <p className="mv-label">{item.category} · {item.type}</p>
              <h1 className="mt-3 font-display text-4xl leading-[1.08] text-ink sm:text-5xl" data-testid="shop-item-title">{item.title}</h1>
              <p className="mt-6 leading-relaxed text-smoke">{item.description}</p>

              {(item.personalization || []).length > 0 && (
                <div className="mt-8">
                  <p className="mv-label">We personalize this together — details to discuss</p>
                  <ul className="mt-3 space-y-1.5 text-sm text-smoke" data-testid="shop-item-personalization">
                    {item.personalization.map((p) => (
                      <li key={p} className="flex items-baseline gap-3">
                        <span className="h-px w-5 flex-none bg-olive" aria-hidden="true" /> {p}
                      </li>
                    ))}
                  </ul>
                  <label htmlFor="item-options" className="mv-label mt-6">Your notes for this piece (optional)</label>
                  <input
                    id="item-options"
                    data-testid="shop-item-options-input"
                    className="mv-input"
                    value={options}
                    onChange={(e) => setOptions(e.target.value)}
                    placeholder="Palette, monogram, wording, quantities…"
                    maxLength={200}
                  />
                </div>
              )}

              <div className="mt-8">
                <p className="mv-label">Quantity</p>
                <input
                  type="number"
                  min="1"
                  max="100"
                  data-testid="shop-item-quantity-input"
                  className="mv-input w-24"
                  value={quantity}
                  onChange={(e) => setQuantity(Math.max(1, Math.min(100, parseInt(e.target.value) || 1)))}
                />
              </div>

              <div className="mt-8 flex flex-wrap items-center gap-4">
                <button
                  data-testid="shop-item-add-button"
                  onClick={add}
                  className="rounded-none bg-olive px-10 py-4 text-[12px] uppercase tracking-[0.18em] text-ivory transition-transform duration-300 hover:-translate-y-0.5"
                >
                  Add to My Quote
                </button>
                <Link to="/quote" data-testid="shop-item-customize-link" className="border border-ink/25 px-10 py-4 text-[12px] uppercase tracking-[0.18em] text-ink transition-colors hover:border-olive hover:text-olive">
                  Customize &amp; Inquire
                </Link>
              </div>
              <p className="mt-6 text-[12px] leading-relaxed text-smoke" data-testid="shop-item-pricing-note">
                {item.unit} · Pricing confirmed in your proposal. Presenting materials and finishes is a
                preference to discuss, not guaranteed inventory. Adding to your quote does not reserve
                stock, a date, or a production slot.
              </p>
            </div>
          </FadeUp>
        </div>

        {related.length > 0 && (
          <div className="mt-24">
            <FadeUp>
              <h2 className="font-display text-3xl text-ink sm:text-4xl">Pairs well with</h2>
            </FadeUp>
            <div className="mt-10 grid gap-8 sm:grid-cols-2 lg:grid-cols-3">
              {related.map((r, i) => (
                <FadeUp key={r.slug} delay={i * 0.08}>
                  <ProductCard item={r} />
                </FadeUp>
              ))}
            </div>
          </div>
        )}
      </section>
    </div>
  );
}
